import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import {
  api,
  API_BASE,
  Document as DocumentType,
  getPageMappings,
  PageMapping,
  getDocumentPagesMetadata,
  getPageImageUrl,
  DocumentPagesMetadata
} from '../api/client';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

// Simple WebP Image Viewer - just scrollable images
interface WebPViewerProps {
  documentId: number;
  allowedPages: number[];
  onBack: () => void;
}

function WebPViewer({ documentId, allowedPages, onBack }: WebPViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const thumbnailStripRef = useRef<HTMLDivElement>(null);
  const isMobile = useRef<boolean>(false);

  // Detect mobile on mount
  useEffect(() => {
    isMobile.current = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                       window.matchMedia('(pointer: coarse)').matches;
  }, []);

  // Calculate fit-to-width scale when images load
  const calculateFitScale = useCallback(() => {
    setScale(1);
  }, []);

  // Reset to fit scale when document changes
  useEffect(() => {
    const timer = setTimeout(calculateFitScale, 100);
    return () => clearTimeout(timer);
  }, [documentId, allowedPages, calculateFitScale]);

  // Handle mouse wheel zoom - only when Ctrl/Cmd is pressed
  const handleWheel = (e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) {
      return;
    }
    
    e.preventDefault();
    const zoomSensitivity = 0.1;
    const delta = -e.deltaY * zoomSensitivity;
    const newScale = Math.max(0.1, Math.min(10, scale * (1 + delta)));
    setScale(newScale);
  };

  // Reset zoom to fit
  const handleResetZoom = () => {
    setScale(1);
  };

  // Zoom in/out buttons
  const zoomIn = () => {
    setScale(s => Math.min(10, s * 1.25));
  };
  
  const zoomOut = () => {
    setScale(s => Math.max(0.1, s / 1.25));
  };

  const handleThumbnailClick = (pageNum: number) => {
    setCurrentPage(pageNum);
    const element = document.getElementById(`page-${pageNum}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (thumbnailStripRef.current) {
      const btn = thumbnailStripRef.current.querySelector(`[data-page="${pageNum}"]`) as HTMLElement;
      if (btn) {
        btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  };

  // Mobile: Simple stacked images view with native browser zoom/scroll
  if (isMobile.current) {
    return (
      <div style={{ minHeight: '100vh', background: '#444' }}>
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          padding: '8px 16px',
          background: '#333',
          color: '#fff',
          display: 'flex',
          alignItems: 'center'
        }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '16px' }}>
            ← Back
          </button>
          <span style={{ marginLeft: '16px' }}>{allowedPages.length} page{allowedPages.length !== 1 ? 's' : ''}</span>
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          padding: '16px'
        }}>
          {allowedPages.map((pageNum) => (
            <img
              key={pageNum}
              src={getPageImageUrl(documentId, pageNum)}
              alt={`Page ${pageNum}`}
              style={{
                width: '100%',
                maxWidth: '100%',
                height: 'auto',
                display: 'block',
                background: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  // Desktop: Scrollable view with thumbnail navigation
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header with controls */}
      <div style={{
        padding: '8px 16px',
        background: '#333',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        flexShrink: 0
      }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '16px' }}>
          ← Back
        </button>
        <span>{allowedPages.length} page{allowedPages.length !== 1 ? 's' : ''}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={zoomOut}
            style={{
              padding: '6px 12px',
              background: '#6c757d',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            -
          </button>
          <span style={{ minWidth: '60px', textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={zoomIn}
            style={{
              padding: '6px 12px',
              background: '#6c757d',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            +
          </button>
          <button
            onClick={handleResetZoom}
            style={{
              padding: '6px 12px',
              background: '#17a2b8',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginLeft: '8px'
            }}
          >
            Fit
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div
        ref={containerRef}
        onWheel={handleWheel}
        style={{
          flex: 1,
          overflowY: 'auto',
          background: '#444',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          padding: '16px'
        }}>
          {allowedPages.map((pageNum) => (
            <div
              key={pageNum}
              id={`page-${pageNum}`}
              onClick={() => setCurrentPage(pageNum)}
              style={{
                width: '100%',
                maxWidth: `${scale * 100}%`,
                cursor: 'pointer',
                border: currentPage === pageNum ? '2px solid #0066cc' : '2px solid transparent',
                boxSizing: 'border-box'
              }}
            >
              <img
                src={getPageImageUrl(documentId, pageNum)}
                alt={`Page ${pageNum}`}
                draggable={false}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  background: '#fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Thumbnail Strip */}
      {allowedPages.length > 1 && (
        <div
          ref={thumbnailStripRef}
          style={{
            height: '80px',
            background: '#222',
            borderTop: '1px solid #333',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px',
            overflowX: 'auto',
            overflowY: 'hidden',
            flexShrink: 0
          }}
        >
          {allowedPages.map((pageNum) => (
            <button
              key={pageNum}
              data-page={pageNum}
              onClick={() => handleThumbnailClick(pageNum)}
              style={{
                flexShrink: 0,
                width: '60px',
                height: '60px',
                padding: 0,
                border: currentPage === pageNum ? '2px solid #0066cc' : '2px solid transparent',
                borderRadius: '4px',
                background: '#333',
                cursor: 'pointer',
                overflow: 'hidden'
              }}
            >
              <img
                src={getPageImageUrl(documentId, pageNum)}
                alt={`Page ${pageNum}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Original PDF Viewer Component (fallback)
interface PDFViewerFallbackProps {
  documentId: number;
  allowedPages: number[];
  showFull: boolean;
  onBack: () => void;
  onPageLoad: (numPages: number) => void;
}

function PDFViewerFallback({ documentId, allowedPages, showFull, onBack, onPageLoad }: PDFViewerFallbackProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState<number>(1.0);
  const [translate, setTranslate] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [pageWidth, setPageWidth] = useState<number | null>(null);
  const [localAllowedPages, setLocalAllowedPages] = useState<number[]>(allowedPages);
  const lastTouchRef = useRef<{ x: number; y: number } | null>(null);
  const pinchStartDistRef = useRef<number | null>(null);
  const pinchStartScaleRef = useRef<number>(1);

  function onDocumentLoadSuccess({ numPages: totalPages }: { numPages: number }) {
    onPageLoad(totalPages);
    // If no allowed pages were set (no mappings found or showing full), allow all pages
    if (allowedPages.length === 0 || showFull) {
      const allPages = Array.from({ length: totalPages }, (_, i) => i + 1);
      setLocalAllowedPages(allPages);
    }
  }

  // Called when first page loads - captures actual page dimensions
  function onPageLoadSuccess(page: { width: number; height: number }) {
    if (!pageWidth) {
      setPageWidth(page.width);
      calculateFitToWidth(page.width);
    }
  }

  // Calculate fit-to-width scale
  function calculateFitToWidth(actualPageWidth?: number) {
    const container = containerRef.current;
    if (!container) return;

    const containerWidth = container.clientWidth - 48;
    const pdfWidth = actualPageWidth || pageWidth || 612;
    const fitScale = containerWidth / pdfWidth;
    setScale(Math.max(0.1, fitScale));
    setTranslate({ x: 0, y: 0 });
  }

  // Recalculate fit on window resize
  useEffect(() => {
    const handleResize = () => {
      if (pageWidth) {
        calculateFitToWidth(pageWidth);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [pageWidth]);

  // Get distance between two touch points
  const getTouchDistance = (touches: React.TouchList): number => {
    const touch1 = touches.item(0);
    const touch2 = touches.item(1);
    if (!touch1 || !touch2) return 0;
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Handle touch start
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      // Single touch - start panning
      lastTouchRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY
      };
      setIsDragging(true);
    } else if (e.touches.length === 2) {
      // Two finger touch - start pinch
      e.preventDefault();
      lastTouchRef.current = null;
      pinchStartDistRef.current = getTouchDistance(e.touches);
      pinchStartScaleRef.current = scale;
    }
  };

  // Handle touch move
  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && isDragging && lastTouchRef.current) {
      // Panning with single finger
      const dx = e.touches[0].clientX - lastTouchRef.current.x;
      const dy = e.touches[0].clientY - lastTouchRef.current.y;

      setTranslate(prev => ({
        x: prev.x + dx,
        y: prev.y + dy
      }));

      lastTouchRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY
      };
    } else if (e.touches.length === 2 && pinchStartDistRef.current) {
      // Pinch zoom
      e.preventDefault();
      const currentDist = getTouchDistance(e.touches);
      const scaleFactor = currentDist / pinchStartDistRef.current;
      const newScale = pinchStartScaleRef.current * scaleFactor;

      setScale(newScale);
    }
  };

  // Handle touch end
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (e.touches.length === 0) {
      setIsDragging(false);
      lastTouchRef.current = null;
      pinchStartDistRef.current = null;
    } else if (e.touches.length === 1) {
      // If one finger remains, switch back to pan mode
      pinchStartDistRef.current = null;
      lastTouchRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY
      };
      setIsDragging(true);
    }
  };

  // Handle mouse wheel zoom
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const zoomSensitivity = 0.001;
    const delta = -e.deltaY * zoomSensitivity;
    const newScale = Math.max(0.1, scale * (1 + delta));

    setScale(newScale);
  };

  // Mouse drag support for desktop
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0) { // Left click only
      lastTouchRef.current = {
        x: e.clientX,
        y: e.clientY
      };
      setIsDragging(true);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && lastTouchRef.current) {
      const dx = e.clientX - lastTouchRef.current.x;
      const dy = e.clientY - lastTouchRef.current.y;

      setTranslate(prev => ({
        x: prev.x + dx,
        y: prev.y + dy
      }));

      lastTouchRef.current = {
        x: e.clientX,
        y: e.clientY
      };
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    lastTouchRef.current = null;
  };

  // Zoom controls
  const zoomIn = () => setScale(s => s * 1.25);
  const zoomOut = () => setScale(s => Math.max(0.1, s / 1.25));

  const pagesToShow = localAllowedPages.length > 0 ? localAllowedPages : allowedPages;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        padding: '8px 16px',
        background: '#333',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        flexShrink: 0
      }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '16px' }}>
          ← Back
        </button>
        <span>{pagesToShow.length} page{pagesToShow.length !== 1 ? 's' : ''}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={zoomOut}
            style={{
              padding: '6px 12px',
              background: '#6c757d',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            -
          </button>
          <span style={{ minWidth: '60px', textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={zoomIn}
            style={{
              padding: '6px 12px',
              background: '#6c757d',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            +
          </button>
          <button
            onClick={() => calculateFitToWidth()}
            style={{
              padding: '6px 12px',
              background: '#17a2b8',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginLeft: '8px'
            }}
          >
            Fit
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{
          flex: 1,
          overflow: 'hidden',
          background: '#666',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'none',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          WebkitUserSelect: 'none'
        }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px',
          padding: '24px',
          transformOrigin: 'center top',
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`
        }}>
          <Document
            file={`${API_BASE}/api/pdf/document/${documentId}`}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={<div style={{ color: '#fff', textAlign: 'center' }}>Loading PDF...</div>}
            error={<div style={{ color: '#fff', textAlign: 'center' }}>Failed to load PDF</div>}
          >
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '24px'
            }}>
              {pagesToShow.map((pageNum, index) => (
                <div
                  key={pageNum}
                  style={{
                    background: '#fff',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    transformOrigin: 'center'
                  }}
                >
                  <Page
                    pageNumber={pageNum}
                    scale={1}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    onLoadSuccess={index === 0 ? onPageLoadSuccess : undefined}
                  />
                </div>
              ))}
            </div>
          </Document>
        </div>
      </div>
    </div>
  );
}

// Main PDFViewer component that decides between WebP and PDF
export function PDFViewer() {
  const { projectId, roomId, documentSetId, documentId: documentIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [documentId, setDocumentId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [allowedPages, setAllowedPages] = useState<number[]>([]);
  const [pagesMetadata, setPagesMetadata] = useState<DocumentPagesMetadata | null>(null);
  const [useWebP, setUseWebP] = useState<boolean>(false);

  const showFull = searchParams.get('full') === 'true';

  // Load document ID and page mappings
  useEffect(() => {
    const loadDocument = async () => {
      let docId: number | null = null;

      if (documentIdParam) {
        docId = parseInt(documentIdParam, 10);
        setDocumentId(docId);
      } else if (documentSetId) {
        try {
          const response = await api.get(`/api/document-sets/${documentSetId}/documents`);
          const documents: DocumentType[] = response.data;
          if (documents.length > 0) {
            docId = documents[0].id;
            setDocumentId(docId);
          }
        } catch (error) {
          console.error('Failed to load documents:', error);
        }
      }

      if (!docId) {
        setLoading(false);
        return;
      }

      // Check if WebP pages are available
      try {
        const metadata = await getDocumentPagesMetadata(docId);
        setPagesMetadata(metadata);

        if (metadata.conversion_status === 'ready' && metadata.pages.length > 0) {
          setUseWebP(true);

          // If showing full document, use all pages from metadata
          if (showFull) {
            setAllowedPages(metadata.pages);
            setLoading(false);
            return;
          }

          // Load page mappings for the room if we have a roomId
          if (roomId) {
            try {
              const mappings = await getPageMappings(Number(roomId));
              const pagesForDoc = mappings
                .filter((m: PageMapping) => m.document_id === docId)
                .map((m: PageMapping) => m.page_number)
                .sort((a: number, b: number) => a - b);

              const uniquePages = Array.from(new Set(pagesForDoc));
              // Only use pages that exist in metadata
              const validPages = uniquePages.filter(p => metadata.pages.includes(p));
              setAllowedPages(validPages.length > 0 ? validPages : metadata.pages);
            } catch (error) {
              console.error('Failed to load page mappings:', error);
              setAllowedPages(metadata.pages);
            }
          } else {
            setAllowedPages(metadata.pages);
          }
        } else {
          // Fall back to PDF viewer
          setUseWebP(false);

          if (showFull) {
            setAllowedPages([]);
            setLoading(false);
            return;
          }

          // Load page mappings for PDF fallback
          if (roomId) {
            try {
              const mappings = await getPageMappings(Number(roomId));
              const pagesForDoc = mappings
                .filter((m: PageMapping) => m.document_id === docId)
                .map((m: PageMapping) => m.page_number)
                .sort((a: number, b: number) => a - b);

              const uniquePages = Array.from(new Set(pagesForDoc));
              setAllowedPages(uniquePages);
            } catch (error) {
              console.error('Failed to load page mappings:', error);
            }
          }
        }
      } catch (error) {
        console.error('Failed to load pages metadata, falling back to PDF:', error);
        setUseWebP(false);

        if (showFull) {
          setAllowedPages([]);
        } else if (roomId && docId) {
          try {
            const mappings = await getPageMappings(Number(roomId));
            const pagesForDoc = mappings
              .filter((m: PageMapping) => m.document_id === docId)
              .map((m: PageMapping) => m.page_number)
              .sort((a: number, b: number) => a - b);

            const uniquePages = Array.from(new Set(pagesForDoc));
            setAllowedPages(uniquePages);
          } catch (mappingError) {
            console.error('Failed to load page mappings:', mappingError);
          }
        }
      }

      setLoading(false);
    };

    loadDocument();
  }, [documentSetId, documentIdParam, roomId, showFull]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handlePDFPageLoad = useCallback((numPages: number) => {
    if (allowedPages.length === 0) {
      const allPages = Array.from({ length: numPages }, (_, i) => i + 1);
      setAllowedPages(allPages);
    }
  }, [allowedPages.length]);

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#666' }}>
        <div style={{ color: '#fff' }}>Loading...</div>
      </div>
    );
  }

  if (!documentId) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{
          padding: '8px 16px',
          background: '#333',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          <button onClick={handleBack} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}>
            &larr; Back
          </button>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#666' }}>
          <div style={{ color: '#fff' }}>No document found</div>
        </div>
      </div>
    );
  }

  // Use WebP viewer if conversion is ready
  if (useWebP && allowedPages.length > 0) {
    return (
      <WebPViewer
        documentId={documentId}
        allowedPages={allowedPages}
        onBack={handleBack}
      />
    );
  }

  // Fall back to PDF viewer
  return (
    <PDFViewerFallback
      documentId={documentId}
      allowedPages={allowedPages}
      showFull={showFull}
      onBack={handleBack}
      onPageLoad={handlePDFPageLoad}
    />
  );
}
